// ---------------------------------------------------------------------------
// AboutPane — aba "Regras de reuso" do InputDrawerV3.
// ---------------------------------------------------------------------------
// Concentra os toggles de reuso manual/automático (``reuse_allowed`` /
// ``reuse_auto``). Propriedades e instruções vivem na aba irmã
// ``InputInfoPane`` ("Instruções").
// ---------------------------------------------------------------------------

import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

import { toast } from "react-toastify";

import useDrawerStyles from "../styles";
import useThemeColors from "../../../../../hooks/useThemeColors";
import useNativeWheelScroll from "../../hooks/useNativeWheelScroll";
import { useOverlayScrollStyles } from "../../styles";
import PaneErrorBanner from "./PaneErrorBanner";
import Toggle from "../Toggle";
import DrawerPaneLoadingLayout from "../../DrawerPaneLoadingLayout";
import { saveInputReuseConfigV3 } from "../../../../../services/inputsV3Service";
import { isManualReuseApplicable } from "../../../../../services/inputsV3Volatility";

const notify = (text, type = "info", autoClose = 3000) =>
  toast(text, {
    position: "top-right",
    autoClose,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: false,
    type,
  });

const REUSE_TOGGLE_ROLES = new Set(["DEV", "IMG", "CAD"]);

function PaneTitle({ classes, title }) {
  return (
    <div className={classes.paneLoadingLayoutHead}>
      <div className={classes.paneLoadingLayoutTitle}>{title}</div>
    </div>
  );
}

PaneTitle.propTypes = {
  classes: PropTypes.object.isRequired,
  title: PropTypes.string.isRequired,
};

const AboutPane = ({
  input,
  userRole,
  drawerDetails,
  onReuseConfigApplied,
}) => {
  const { t } = useTranslation();
  const { clientColor } = useThemeColors();
  const classes = useDrawerStyles({ clientColor });
  const overlayScrollClasses = useOverlayScrollStyles();
  const wheelRef = useNativeWheelScroll();
  const canEditReuse = REUSE_TOGGLE_ROLES.has(userRole);

  const aboutFromDetails = drawerDetails?.about || null;
  const reuseConfigLoaded =
    !!aboutFromDetails && aboutFromDetails.meta_name === input?.meta_name;
  const [reuseAllowed, setReuseAllowed] = useState(null);
  const [reuseAuto, setReuseAuto] = useState(null);
  const [reuseSaving, setReuseSaving] = useState(false);

  useEffect(() => {
    setReuseAllowed(null);
    setReuseAuto(null);
    setReuseSaving(false);
  }, [input?.meta_name]);

  useEffect(() => {
    if (reuseConfigLoaded) {
      setReuseAllowed(!!aboutFromDetails.reuse_allowed);
      setReuseAuto(!!aboutFromDetails.reuse_auto);
    }
  }, [
    reuseConfigLoaded,
    aboutFromDetails?.reuse_allowed,
    aboutFromDetails?.reuse_auto,
  ]);

  const handleToggleReuseAllowed = () => {
    if (!canEditReuse || reuseSaving || !reuseConfigLoaded) return;
    const previousAllowed = !!reuseAllowed;
    const previousAuto = !!reuseAuto;
    const next = !previousAllowed;
    setReuseAllowed(next);
    if (!next) setReuseAuto(false);
    setReuseSaving(true);
    saveInputReuseConfigV3({
      metaName: input?.meta_name,
      reuseAllowed: next,
      reuseAuto: next ? previousAuto : false,
    })
      .then(() => {
        // Reflete a mudanca nos consumidores irmaos (toolbar "Usar mes
        // anterior" / HistoryPane "Usar arquivo") assim que a API
        // retorna, sem esperar o re-fetch do polling.
        if (typeof onReuseConfigApplied === "function") {
          onReuseConfigApplied({
            metaName: input?.meta_name,
            reuseAllowed: next,
            reuseAuto: next ? previousAuto : false,
          });
        }
        if (typeof drawerDetails?.refresh === "function") {
          drawerDetails.refresh();
        }
      })
      .catch((err) => {
        setReuseAllowed(previousAllowed);
        setReuseAuto(previousAuto);
        const code = err?.response?.data?.error_code;
        const friendly =
          code === "forbidden_save_reuse_config"
            ? t("modeling_inputs_v3.about_pane.reuse_forbidden")
            : t("modeling_inputs_v3.about_pane.reuse_save_error");
        notify(friendly, "error", 3500);
      })
      .finally(() => setReuseSaving(false));
  };

  const handleToggleReuseAuto = () => {
    if (!canEditReuse || reuseSaving || !reuseAllowed || !reuseConfigLoaded)
      return;
    const previousAuto = !!reuseAuto;
    const next = !previousAuto;
    setReuseAuto(next);
    setReuseSaving(true);
    saveInputReuseConfigV3({
      metaName: input?.meta_name,
      reuseAuto: next,
    })
      .then(() => {
        // ``reuse_auto`` so pode ser ligado com ``reuse_allowed`` ja
        // verdadeiro (guardado acima), entao propagamos allowed=true.
        if (typeof onReuseConfigApplied === "function") {
          onReuseConfigApplied({
            metaName: input?.meta_name,
            reuseAllowed: true,
            reuseAuto: next,
          });
        }
        if (typeof drawerDetails?.refresh === "function") {
          drawerDetails.refresh();
        }
      })
      .catch((err) => {
        setReuseAuto(previousAuto);
        const code = err?.response?.data?.error_code;
        const friendly =
          code === "forbidden_save_reuse_config"
            ? t("modeling_inputs_v3.about_pane.reuse_forbidden")
            : code === "invalid_reuse_combination"
              ? t("modeling_inputs_v3.about_pane.reuse_auto_conflict")
              : t("modeling_inputs_v3.about_pane.reuse_save_error");
        notify(friendly, "error", 3500);
      })
      .finally(() => setReuseSaving(false));
  };

  const isNonMonthlyVolatility =
    reuseConfigLoaded && !isManualReuseApplicable(aboutFromDetails?.volatility);
  const reuseDisabled =
    !canEditReuse ||
    reuseSaving ||
    !reuseConfigLoaded ||
    isNonMonthlyVolatility;

  const showFullPaneLoading =
    !drawerDetails?.error &&
    (Boolean(drawerDetails?.loading) ||
      !reuseConfigLoaded ||
      !drawerDetails?.runtimeInput);

  if (showFullPaneLoading) {
    return (
      <DrawerPaneLoadingLayout
        variant="input"
        scrollRef={wheelRef}
        overlayScrollClass={overlayScrollClasses.overlayScroll}
        title={t("modeling_inputs_v3.about_pane.title")}
        message={t("modeling_inputs_v3.about_pane.loading_message")}
      />
    );
  }

  return (
    <div
      ref={wheelRef}
      className={`${classes.paneScroll} ${overlayScrollClasses.overlayScroll}`}
    >
      <PaneTitle
        classes={classes}
        title={t("modeling_inputs_v3.about_pane.title")}
      />
      {drawerDetails?.error ? (
        <PaneErrorBanner
          title={t("modeling_inputs_v3.about_pane.error_title")}
          message={t("modeling_inputs_v3.about_pane.error_message")}
          detail={
            drawerDetails.error?.response?.data?.error_code ||
            drawerDetails.error?.message ||
            null
          }
          onRetry={
            typeof drawerDetails.refresh === "function"
              ? drawerDetails.refresh
              : null
          }
          retrying={Boolean(drawerDetails.loading)}
        />
      ) : null}

      <div
        className={classes.section}
        style={{
          opacity: reuseSaving ? 0.55 : 1,
          transition: "opacity 160ms ease",
        }}
      >
        <div className={classes.sectionTitle}>
          {t("modeling_inputs_v3.about_pane.section_reuse")}
        </div>
        <div
          className={classes.topic}
          style={
            isNonMonthlyVolatility
              ? { opacity: 0.5, transition: "opacity 160ms ease" }
              : { opacity: 1, transition: "opacity 160ms ease" }
          }
        >
          <div className={classes.topicName}>
            {t("modeling_inputs_v3.about_pane.reuse_allowed_label")}
          </div>
          <div className={classes.topicContent}>
            <Toggle
              checked={!!reuseAllowed}
              onChange={!reuseDisabled ? handleToggleReuseAllowed : undefined}
              disabled={reuseDisabled}
              label={
                isNonMonthlyVolatility
                  ? t("modeling_inputs_v3.about_pane.reuse_not_applicable")
                  : reuseAllowed
                    ? t("modeling_inputs_v3.about_pane.reuse_allowed_on")
                    : t("modeling_inputs_v3.about_pane.reuse_allowed_off")
              }
              name="reuse_allowed"
            />
          </div>
        </div>
        <div
          className={classes.topic}
          style={
            !reuseAllowed || isNonMonthlyVolatility
              ? { opacity: 0.5, transition: "opacity 160ms ease" }
              : { opacity: 1, transition: "opacity 160ms ease" }
          }
        >
          <div className={classes.topicName}>
            {t("modeling_inputs_v3.about_pane.reuse_auto_label")}
          </div>
          <div className={classes.topicContent}>
            <Toggle
              checked={!!reuseAuto}
              onChange={
                canEditReuse &&
                reuseAllowed &&
                !reuseSaving &&
                !isNonMonthlyVolatility
                  ? handleToggleReuseAuto
                  : undefined
              }
              disabled={reuseDisabled || !reuseAllowed}
              label={
                isNonMonthlyVolatility
                  ? t("modeling_inputs_v3.about_pane.reuse_not_applicable")
                  : reuseAuto
                    ? t("modeling_inputs_v3.about_pane.reuse_auto_on")
                    : reuseAllowed
                      ? t("modeling_inputs_v3.about_pane.reuse_auto_available")
                      : t(
                          "modeling_inputs_v3.about_pane.reuse_auto_unavailable",
                        )
              }
              name="reuse_auto"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

AboutPane.propTypes = {
  input: PropTypes.object,
  userRole: PropTypes.string,
  drawerDetails: PropTypes.shape({
    loading: PropTypes.bool,
    error: PropTypes.any,
    about: PropTypes.shape({
      reuse_allowed: PropTypes.bool,
      reuse_auto: PropTypes.bool,
      volatility: PropTypes.string,
      meta_name: PropTypes.string,
    }),
    runtimeInput: PropTypes.shape({ meta_name: PropTypes.string }),
    refresh: PropTypes.func,
  }),
  onReuseConfigApplied: PropTypes.func,
};

export default AboutPane;
